import * as React from 'react'
import Box from '@mui/material/Box'
import { addDays } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { compareEventsByStart } from '../../eventOrder'
import { daysCovered } from '../../eventSpan'
import { useToday } from '../../hooks/useToday'
import { WeekHeaderRow } from './WeekHeaderRow'
import { WeekBodyRow } from './WeekBodyRow'
import { TimeScroller } from './TimeScroller'
import { STICKY_HEADER_SX } from './stickyHeader'

type Props = {
  /** Week start (Sunday, midnight). */
  weekStart: Date
  events: CalendarEvent[]
  onShowDay?: (day: Date) => void
}

export function WeekGrid({ weekStart, events, onShowDay }: Props) {
  const today = useToday()
  const days = React.useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  )

  // All-day events go to the header as bars spanning their days; timed events
  // land in every body column they touch.
  const { allDayEvents, timedByDay } = React.useMemo(() => {
    const timed: CalendarEvent[][] = days.map(() => [])
    for (const event of [...events].sort(compareEventsByStart)) {
      if (event.allDay) continue
      for (const i of daysCovered(event, days)) timed[i].push(event)
    }
    return {
      allDayEvents: events.filter((event) => event.allDay),
      timedByDay: timed,
    }
  }, [days, events])

  return (
    <TimeScroller days={days} label="Week view">
      <Box sx={STICKY_HEADER_SX}>
        <WeekHeaderRow
          days={days}
          allDayEvents={allDayEvents}
          onShowDay={onShowDay}
        />
      </Box>
      <WeekBodyRow
        days={days}
        timedByDay={timedByDay}
        today={today}
        onShowDay={onShowDay}
      />
    </TimeScroller>
  )
}
